import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import { useIndexProps } from "./page-props"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { educatorsTeamApplicationResponse } from "@/routes"
import { BadgeCheck, Ban, ExternalLink, Inbox } from "lucide-react"
import { useTranslation } from "react-i18next"
import { PageBody } from "@/components/ui/layout"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import {
  ConfirmationDialog,
  ConfirmationDialogTrigger,
} from "@/components/shared/confirmation-dialog"
import { Badge } from "@/components/ui/badge"
import { Link } from "@/lib/page-rendering"

export default function Page() {
  const { t } = useTranslation(["team_applications", "common"])
  const { team_applications, team_name } = useIndexProps()

  return (
    <>
      <EducatorPageChrome records={{ "educators/teams/edit": team_name }} />
      <PageBody role="region">
        <Card data-testid="team-application-list">
          <CardHeader>
            <CardTitle>{team_name}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-6 py-6">
            {team_applications.length == 0 ? (
              <Empty className="col-span-3">
                <EmptyHeader>
                  <EmptyMedia
                    variant="icon"
                    className="text-muted size-20 bg-white"
                  >
                    <Inbox className="size-16" />
                  </EmptyMedia>
                  <EmptyTitle>{t("index.empty_list_title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("index.empty_list_description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              team_applications.map((team_application) => (
                <Card className="w-full" data-testid="team-application-item">
                  <CardHeader className="flex flex-row items-center justify-between align-bottom">
                    <CardTitle data-testid="teacher-name">
                      {team_application.teacher_name}
                    </CardTitle>
                    <Badge>
                      {t(
                        `team_applications:index.status.${team_application.state}`,
                      )}
                    </Badge>
                  </CardHeader>
                  <CardFooter className="flex flex-row flex-wrap gap-4">
                    <a
                      href={team_application.teacher_profile_url}
                      target="_blank"
                      className={buttonVariants()}
                    >
                      <ExternalLink />
                      {t("index.profile")}
                    </a>
                    {team_application.ready_to_accept && (
                      <ConfirmationDialog
                        title={t("team_applications:index.actions.accept")}
                        description={t(
                          "team_applications:index.actions.accept_description",
                        )}
                        action={
                          <Link
                            href={
                              educatorsTeamApplicationResponse({
                                teamApplicationId: team_application.id,
                              }).url
                            }
                            method="post"
                            as={Button}
                            data={{
                              response: "accept",
                            }}
                          >
                            {t("team_applications:index.actions.accept")}
                          </Link>
                        }
                      >
                        <ConfirmationDialogTrigger asChild>
                          <Button>
                            <BadgeCheck />
                            {t("team_applications:index.actions.accept")}
                          </Button>
                        </ConfirmationDialogTrigger>
                      </ConfirmationDialog>
                    )}
                    <ConfirmationDialog
                      title={t("team_applications:index.actions.reject")}
                      description={t(
                        "team_applications:index.actions.reject_description",
                      )}
                      action={
                        <Link
                          className={buttonVariants({ variant: "destructive" })}
                          href={
                            educatorsTeamApplicationResponse({
                              teamApplicationId: team_application.id,
                            }).url
                          }
                          method="patch"
                          as={Button}
                          data={{
                            response: "reject",
                          }}
                        >
                          {t("team_applications:index.actions.reject")}
                        </Link>
                      }
                    >
                      <ConfirmationDialogTrigger asChild>
                        <Button variant={"destructive-outline"}>
                          <Ban />
                          {t("team_applications:index.actions.reject")}
                        </Button>
                      </ConfirmationDialogTrigger>
                    </ConfirmationDialog>
                  </CardFooter>
                </Card>
              ))
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}
